/*
 * WhatsApp do tema (snippets/whatsapp-chat.liquid e snippets/whatsapp-product-button.liquid).
 * Monta a mensagem (na página de produto, com produto, variante e preço selecionados), pede o nome
 * quando o pré-atendimento está ligado, registra o clique e abre o WhatsApp numa nova aba.
 */
if (!customElements.get('whatsapp-chat')) {
  customElements.define(
    'whatsapp-chat',
    class WhatsappChat extends HTMLElement {
      constructor() {
        super();

        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onOverlayClick = this.onOverlayClick.bind(this);
        this.close = this.close.bind(this);
      }

      connectedCallback() {
        this.overlay = this.querySelector('[data-whatsapp-overlay]');
        this.form = this.querySelector('[data-whatsapp-form]');
        this.error = this.querySelector('[data-whatsapp-error]');
        this.cancel = this.querySelector('[data-whatsapp-cancel]');

        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeydown);
        this.form?.addEventListener('submit', this.onSubmit);
        this.cancel?.addEventListener('click', this.close);
        this.overlay?.addEventListener('click', this.onOverlayClick);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeydown);
        this.form?.removeEventListener('submit', this.onSubmit);
        this.cancel?.removeEventListener('click', this.close);
        this.overlay?.removeEventListener('click', this.onOverlayClick);
      }

      onDocumentClick(event) {
        const trigger = event.target.closest('[data-whatsapp-trigger]');
        if (!trigger) return;

        event.preventDefault();
        this.pending = { origin: trigger.dataset.whatsappTrigger, context: this.productContext(trigger) };

        if (this.dataset.prechat === 'true' && this.overlay) {
          this.open();
        } else {
          this.send('');
        }
      }

      productContext(trigger) {
        const source = trigger.dataset.productJson && document.getElementById(trigger.dataset.productJson);
        if (!source) return null;

        try {
          const product = JSON.parse(source.textContent);
          const form = document.getElementById(trigger.dataset.productFormId);
          const selectedId = form?.querySelector('[name="id"]')?.value;
          const variantId = String(selectedId || product.defaultVariant);
          const variant = product.variants[variantId] || product.variants[product.defaultVariant];

          return { id: product.id, title: product.title, variantId, variant };
        } catch (error) {
          console.error(error);
          return null;
        }
      }

      open() {
        this.lastFocus = document.activeElement;
        this.form.reset();
        this.error.hidden = true;
        this.overlay.hidden = false;
        document.documentElement.classList.add('whatsapp-chat-open');
        this.setBackgroundInert(true);
        requestAnimationFrame(() => this.form.elements.whatsapp_name?.focus());
      }

      close() {
        if (!this.overlay || this.overlay.hidden) return;

        this.overlay.hidden = true;
        document.documentElement.classList.remove('whatsapp-chat-open');
        this.setBackgroundInert(false);
        this.lastFocus?.focus?.();
      }

      // Com o pré-atendimento aberto, o resto da página fica inerte (fora do Tab e do leitor de tela),
      // como no diálogo modal; os elementos que já estavam inertes não são tocados
      setBackgroundInert(inert) {
        if (inert) {
          const targets = [];
          for (let node = this.overlay; node && node !== document.body; node = node.parentElement) {
            Array.from(node.parentElement?.children ?? []).forEach((sibling) => {
              if (sibling === node || sibling.inert || ['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE'].includes(sibling.tagName)) return;
              sibling.inert = true;
              targets.push(sibling);
            });
          }
          this.inertTargets = targets;
        } else {
          this.inertTargets?.forEach((element) => { element.inert = false; });
          this.inertTargets = null;
        }
      }

      // Reserva para navegadores sem inert: o Tab circula só entre os campos e botões do diálogo
      trapTab(event) {
        const focusable = Array.from(this.overlay.querySelectorAll('input, button, a[href], textarea, select'))
          .filter((element) => !element.disabled && !element.hidden && element.offsetParent !== null);
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || !this.overlay.contains(document.activeElement))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !this.overlay.contains(document.activeElement))) {
          event.preventDefault();
          first.focus();
        }
      }

      onOverlayClick(event) {
        if (event.target === this.overlay) this.close();
      }

      onKeydown(event) {
        if (event.key === 'Escape') this.close();
        if (event.key === 'Tab' && this.overlay && !this.overlay.hidden) this.trapTab(event);
      }

      onSubmit(event) {
        event.preventDefault();

        const name = this.form.elements.whatsapp_name.value.trim();
        if (!name) {
          this.error.hidden = false;
          this.form.elements.whatsapp_name.focus();
          return;
        }

        this.close();
        this.send(name);
      }

      buildMessage(name) {
        const context = this.pending?.context;
        let message = (context && this.dataset.productMessage) || this.dataset.message || '';

        if (context) {
          const variant = context.variant;
          const variantTitle = variant && variant.title !== 'Default Title' ? variant.title : '';

          message = message
            .replaceAll('[produto]', context.title)
            .replaceAll('[variante]', variantTitle)
            .replaceAll('[preco]', variant ? variant.price : '')
            .replace(/\s*\(\s*\)/g, '')
            .replace(/ {2,}/g, ' ');
        }

        if (name) {
          const greeting = /^ol[áa][,!]?\s*/i;
          message = greeting.test(message)
            ? message.replace(greeting, `Olá, me chamo ${name}, `)
            : `Olá, me chamo ${name}. ${message}`;
        }

        if (this.dataset.includePage === 'true') {
          const url = new URL(window.location.href);
          url.search = context?.variantId ? `?variant=${context.variantId}` : '';
          url.hash = '';
          message = `${message}\n${url.toString()}`;
        }

        return message.trim();
      }

      send(name) {
        const context = this.pending?.context;
        const message = this.buildMessage(name);
        // api.whatsapp.com, não wa.me: o gatilho do GTM procura "whatsapp" no endereço do link
        const url = `https://api.whatsapp.com/send?phone=${this.dataset.number}&text=${encodeURIComponent(message)}`;

        const detail = {
          origem: this.pending?.origin || 'flutuante',
          tipo_pagina: this.dataset.pageType,
          produto_id: context?.id,
          produto: context?.title,
          variante_id: context?.variantId,
          valor: context?.variant?.value
        };

        window.dataLayer?.push({ event: 'whatsapp_click', whatsapp: detail });
        window.Shopify?.analytics?.publish?.('whatsapp_click', detail);

        // Abre por um link de verdade, escondido: desde a versão 40 do GTM as conversões vêm do whatsapp_click e o
        // gatilho de links ignora a[hidden]; sem o hidden, cada contato contaria duas vezes
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener';
        link.hidden = true;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    }
  );
}
